/**
 * AreaProfilePage — V2 orchestrator for the Area profile.
 *
 * Manages:
 *   - Session & permissions
 *   - Two polling cycles (basic data 600s, reporting data 1200s)
 *   - Tab state (basic, reporting, devices, wellness, logs)
 *   - Drawer/dialog state for all edit flows
 *   - Toast notifications
 *
 * Props:
 *   id — Area ID (from URL param)
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './area-profile-page.css';
import NavigationV2 from '../../../../components/v2/NavigationV2.jsx';
import ProfileBanner from '../../../../components/v2/ProfileBanner.jsx';
import BasicInfoTab from './components/BasicInfoTab.jsx';
import BasicInfoDrawer from './components/BasicInfoDrawer.jsx';
import ProfileReportingTab from '../../../../components/v2/ProfileReportingTab.jsx';
import DevicesTab from './components/DevicesTab.jsx';
import DeviceAssignDrawer from './components/DeviceAssignDrawer.jsx';
import EasyAddDeviceDrawer from '../../../../components/v2/EasyAddDeviceDrawer.jsx';
import DeviceRemoveDialog from './components/DeviceRemoveDialog.jsx';
import WellnessTab from './components/WellnessTab.jsx';
import LogsTab from './components/LogsTab.jsx';
import EventNoteDialog from '../../../../components/v2/EventNoteDialog.jsx';
import ActiveAlertPanel from '../../../../components/v2/ActiveAlertPanel.jsx';
import Toast from '../../../../components/v2/Toast.jsx';
import useToast from '../../../../hooks/useToast.js';

import {
  getSession,
  getCampusId,
  getUserId,
  getSessionId,
  getCampusList,
  updateSessionCampus,
  getRoleWeight,
  getActiveVendorIdList,
  getCampusConfig,
  getCampusTzOffset,
} from '../../../../services/session.js';
import {
  fetchAreaProfile, fetchAreaTypes, fetchBuildings,
  fetchAreaDevices, fetchAvailableDevices, fetchAreaLogs,
  fetchCareflowWindows, fetchRoundsWindows, fetchPreformNotes,
  fetchHourChart, fetchWeekChart, fetchReasonSummary, fetchLimitedEvents,
  editAreaProfile, removeDeviceFromArea, assignDeviceToArea, addEventNote,
  fetchExternalAreaData,
} from './api/areaProfileApi.js';
// Cross-domain import: speakers are sourced via the same getsipdevices proc
// the Devices page uses. Mirrors the merge added on AreasPage.jsx so SIP
// devices can be assigned to areas through this profile page too.
import { fetchSpeakerDevices, addDevice } from '../../../devices/api/devicesApi.js';
import { fetchCallLogsReport } from '../../../reports/call-logs/api/callLogsReportApi.js';
import { useNotifications } from '../../../../contexts/NotificationContext.jsx';
import { alertsForArea, activeAlertDeviceIds } from '../../../../utils/activeAlerts.js';
import {
  iAmHigherAuthority as iAmHigherAuth,
  convertIsoToLocal,
  formatDateIso,
  shiftDateStr,
  getToday,
} from '../../../../libs.jsx';

// Feature-chip identifiers.
// Room Calling requires a Grandstream Speaker attached to the area.
const SPEAKER_MANUFACTURE_ID = 10;
const SPEAKER_DEVICE_TYPE_ID = 2;
// SafelyYou fall protection is keyed off the SafelyYou vendor.
const SAFELYYOU_VENDOR_ID = 17;
// E-call (nurse call) device categories — a room with any of these has e-call.
const ECALL_CATEGORY_IDS = new Set([1, 2, 6, 11, 15, 16]);

// Small inline icons for the feature chips (match the flat V2 stroke style).
const ChipIcon = ({ paths }) => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
       style={{ flexShrink: 0 }}>
    {paths.map((p, i) => <path key={i} d={p} />)}
  </svg>
);
const PHONE_ICON = <ChipIcon paths={['M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.81.36 1.6.7 2.34a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.74-1.74a2 2 0 0 1 2.11-.45c.74.34 1.53.57 2.34.7A2 2 0 0 1 22 16.92z']} />;
const SHIELD_ICON = <ChipIcon paths={['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z', 'M9 12l2 2 4-4']} />;
const BELL_ICON = <ChipIcon paths={['M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9', 'M13.73 21a2 2 0 0 1-3.46 0']} />;

// ==========================================================================
// COMPONENT
// ==========================================================================

const AreaProfilePage = ({ id }) => {
  const navigate = useNavigate();

  // --------------------------------------------------------------------------
  // SESSION
  // --------------------------------------------------------------------------
  const [campusId, setCampusId] = useState(() => getCampusId());
  const campusList = useMemo(() => getCampusList() || [], []);
  // Campus config for the active campus — the one canonical accessor for all
  // campus-specific settings (name, feature flags, timezone, grandstream) used here.
  const campusConfig = useMemo(() => getCampusConfig(campusId), [campusId]);
  const campusName = useMemo(() => campusConfig?.name || getSession()?.campus || '', [campusConfig]);
  const myRoleWeight = useMemo(() => getRoleWeight(), []);
  const editRights = useMemo(() => iAmHigherAuth(20, myRoleWeight), [myRoleWeight]);
  const devEditRights = useMemo(() => iAmHigherAuth(10, myRoleWeight), [myRoleWeight]);
  const showAdminTabs = useMemo(() => iAmHigherAuth(80, myRoleWeight), [myRoleWeight]);
  // Grandstream-only: speakers come from a separate stored proc and are
  // merged into both the assigned and available device lists below.
  const isGrandstream = useMemo(() => Boolean(campusConfig?.is_grandstream), [campusConfig]);
  // System (manufacture_id=1, device_category_id IN (4,5)) and SIP
  // (manufacture_id=10) devices may only be added/removed by role_weight > 80.
  // Mirrors AreasPage's pattern exactly: restricted devices stay VISIBLE in
  // the assigned list so caregivers see what's attached to the area, but the
  // remove button is gated, and the available pool hides them so they can't
  // be assigned in the first place.
  const canManageRestrictedDevices = useMemo(
    () => iAmHigherAuth(80, myRoleWeight),
    [myRoleWeight]
  );
  // System device types that don't have a per-device profile page and
  // shouldn't be managed by non-admins (repeaters, etc.). The
  // getareadevices proc doesn't reliably return device_category_id, so
  // we also match by device_type_id to keep parity with AreasPage —
  // otherwise restricted devices would slip through when cid is absent.
  const SYSTEM_RESTRICTED_TYPE_IDS = useMemo(() => new Set([10, 11, 56, 57]), []);
  const isRestrictedDevice = useCallback((d) => {
    const mid = parseInt(d?.manufacture_id, 10);
    const cid = parseInt(d?.device_category_id, 10);
    const tid = parseInt(d?.device_type_id, 10);
    if (mid === 10) return true;
    if (mid === 1 && (cid === 4 || cid === 5)) return true;
    if (mid === 1 && SYSTEM_RESTRICTED_TYPE_IDS.has(tid)) return true;
    return false;
  }, [SYSTEM_RESTRICTED_TYPE_IDS]);

  const areaId = useMemo(() => (id ? parseInt(id, 10) : 0), [id]);


  const getSessionParams = useCallback(() => {
    return {
      campusId,
      userId: getUserId(),
      clientId: getSessionId(),
      vendor_ids: getActiveVendorIdList(),
    };
  }, [campusId]);

  // Session feature flags
  const eventNotesActive = campusConfig?.event_notes_active || false;
  const eventNotesPreform = campusConfig?.event_notes_preform || false;
  const eventNotesFreeform = campusConfig?.event_notes_freeform || false;
  const isReasonEditable = campusConfig?.is_reason_editable || false;
  const tzOffset = getCampusTzOffset(campusId);

  /* Live alerts from the campus-wide provider that already backs both alerts
     pages — no fetch or polling of our own. Exactly one array is ever populated
     (cloud on 5.0, legacy on 4.0), so both are handed over and the helpers sort
     out which applies.

     Deliberately NOT calling registerAlertViewer(): that switches on a campus-wide
     reconciliation fetch every 60s, which is for pages showing the whole alert
     list. This page rides the subscription push instead. */
  const { activeCloudAlarms, activeAlarms } = useNotifications();
  /* Alerts filed against this area. Near-area matches are excluded on purpose —
     4.0 sends no near-area id, so including them would show extra rows on 5.0 and
     silently none on 4.0 (see alertsForArea). */
  const areaAlerts = useMemo(
    () => alertsForArea(activeCloudAlarms, activeAlarms, areaId),
    [activeCloudAlarms, activeAlarms, areaId],
  );

  /* Campus-wide, not area-scoped: removing a device mid-alarm can stop its clear
     signal being processed, and that is true of the device wherever its alert is
     filed. */
  const alertedDeviceIds = useMemo(
    () => activeAlertDeviceIds(activeCloudAlarms, activeAlarms),
    [activeCloudAlarms, activeAlarms],
  );

  // --------------------------------------------------------------------------
  // TOAST
  // --------------------------------------------------------------------------
  const { toast, showToast, dismissToast } = useToast(6000);

  // --------------------------------------------------------------------------
  // TABS
  // --------------------------------------------------------------------------
  const [activeTab, setActiveTab] = useState('basic');

  // --------------------------------------------------------------------------
  // CORE STATE
  // --------------------------------------------------------------------------
  const [loading, setLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(true);

  // Basic area data
  const [basicData, setBasicData] = useState({});
  const [areaName, setAreaName] = useState('');
  const [areaTypeId, setAreaTypeId] = useState(0);
  const [areaTypeName, setAreaTypeName] = useState('');
  const [maxOccupancy, setMaxOccupancy] = useState(0);
  const [buildingId, setBuildingId] = useState(0);
  const [buildingName, setBuildingName] = useState('');
  const [residents, setResidents] = useState([]);
  const [areaGroups, setAreaGroups] = useState([]);

  // Dropdowns
  const [areaTypes, setAreaTypes] = useState([]);
  const [buildings, setBuildings] = useState([]);

  // Devices
  const [areaDevices, setAreaDevices] = useState([]);
  const [availableDevices, setAvailableDevices] = useState([]);

  // Wellness
  const [careData, setCareData] = useState([]);
  const [roundsData, setRoundsData] = useState([]);

  // Logs
  const [logData, setLogData] = useState([]);

  // Reporting
  const [hourData, setHourData] = useState({});
  const [weekData, setWeekData] = useState({});
  const [reasonData, setReasonData] = useState({});
  const [eventData, setEventData] = useState([]);
  const [prenoteData, setPrenoteData] = useState([]);

  // --------------------------------------------------------------------------
  // DRAWER / DIALOG STATE
  // --------------------------------------------------------------------------
  const [basicDrawerOpen, setBasicDrawerOpen] = useState(false);
  const [assignDrawerOpen, setAssignDrawerOpen] = useState(false);
  // Quick add-and-assign, launched from inside the assign drawer
  const [easyAddOpen, setEasyAddOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [removeDevice, setRemoveDevice] = useState(null);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteEventIndex, setNoteEventIndex] = useState(null);
  const [saving, setSaving] = useState(false);

  // --------------------------------------------------------------------------
  // DATE RANGE (for event reason summary)
  // --------------------------------------------------------------------------
  const startDate = useMemo(() => formatDateIso(getToday(-28)), []);
  const endDate = useMemo(() => formatDateIso(getToday()), []);

  // --------------------------------------------------------------------------
  // POLLING CYCLE 1: Basic data (600 second interval)
  // --------------------------------------------------------------------------
  const fetchBasicDataCycle = useCallback(async () => {
    const baseFetches = [
      fetchAreaProfile(campusId, areaId),
      fetchAreaTypes(campusId),
      fetchBuildings(campusId),
      fetchAreaDevices(campusId, areaId),
      fetchAvailableDevices(campusId),
      fetchCareflowWindows(campusId),
      fetchRoundsWindows(campusId),
      showAdminTabs ? fetchAreaLogs(areaId) : Promise.resolve([]),
      // Speakers source — skipped (resolves to []) on non-grandstream
      // campuses so the proc isn't called pointlessly.
      isGrandstream ? fetchSpeakerDevices(campusId) : Promise.resolve([]),
    ];

    const [
      profileResult, typesResult, buildingsResult,
      devicesResult, availResult, careResult, roundsResult, logsResult,
      speakersResult,
    ] = await Promise.all(baseFetches);

    // Process profile
    if (profileResult?.length > 0) {
      const b = profileResult[0];
      setBasicData(b);
      setAreaName(b.area_name || '');
      setAreaTypeId(b.area_type_id || 0);
      setAreaTypeName(b.area_type_description || '');
      setMaxOccupancy(b.max_occupancy || 0);
      setBuildingId(b.building_id || 0);
      setBuildingName(b.building_name || '');
      setResidents(b.residents || []);
      setAreaGroups(b.groups || []);
    }

    // Merge speakers into both device lists with device_id dedup. Mirrors
    // the pattern added to AreasPage.jsx — the speaker stored proc returns
    // SIP devices the inovonics proc doesn't surface. For the per-area
    // "assigned" list, only include speakers whose area_id matches this
    // area (the available pool gets all speakers as candidates).
    const speakers = speakersResult || [];

    const mergedAvail = [...(availResult || [])];
    const availSeen = new Set(mergedAvail.map((d) => d.device_id));
    speakers.forEach((d) => {
      if (!availSeen.has(d.device_id)) {
        mergedAvail.push(d);
        availSeen.add(d.device_id);
      }
    });

    const mergedAreaDevices = [...(devicesResult || [])];
    const areaSeen = new Set(mergedAreaDevices.map((d) => d.device_id));
    speakers.forEach((d) => {
      if (Number(d.area_id) === areaId && !areaSeen.has(d.device_id)) {
        mergedAreaDevices.push(d);
        areaSeen.add(d.device_id);
      }
    });

    setAreaTypes(typesResult || []);
    setBuildings(buildingsResult || []);
    // Pass both lists through as-is. Restricted devices stay visible in the
    // assigned table for awareness; the DevicesTab disables the remove
    // button when canManageRestrictedDevices is false. The available list
    // is filtered down in DeviceAssignDrawer instead so a non-admin can't
    // assign a new restricted device.
    setAreaDevices(mergedAreaDevices);
    setAvailableDevices(mergedAvail);
    setCareData(careResult || []);
    setRoundsData(roundsResult || []);
    setLogData(logsResult || []);
    setLoading(false);
  }, [campusId, areaId, showAdminTabs, isGrandstream]);

  useEffect(() => {
    fetchBasicDataCycle();
    const interval = setInterval(fetchBasicDataCycle, 600_000);
    return () => clearInterval(interval);
  }, [fetchBasicDataCycle]);

  // --------------------------------------------------------------------------
  // POLLING CYCLE 2: Reporting data (1200 second interval)
  // --------------------------------------------------------------------------
  const fetchReportingCycle = useCallback(async () => {
    const [hourResult, weekResult, reasonResult, eventsResult, prenotes] = await Promise.all([
      fetchHourChart(campusId, areaId),
      fetchWeekChart(campusId, areaId),
      fetchReasonSummary(campusId, areaId, startDate, endDate),
      fetchLimitedEvents(campusId, areaId),
      (eventNotesPreform ? fetchPreformNotes(campusId) : Promise.resolve([])),
    ]);

    let events = eventsResult || [];

    // Grandstream campuses: fetch call logs spanning the recent events window
    // and merge them into each event row by event_id (mirrors AlertDetailsPage)
    // so the Recent Events table can show a per-event Calls badge. Non-fatal on
    // failure — the events still render, just without call badges.
    if (isGrandstream && events.length) {
      try {
        const dates = events
          .map((e) => (e.event_datetime_local ? String(e.event_datetime_local).slice(0, 10) : ''))
          .filter((d) => d.length === 10)
          .sort();
        if (dates.length) {
          const callResp = await fetchCallLogsReport({
            campusId,
            startDate: shiftDateStr(dates[0], -1),
            endDate: shiftDateStr(dates[dates.length - 1], 1),
          });
          const callRecords = callResp?.records || [];
          if (callRecords.length) {
            const callsByEvent = new Map();
            callRecords.forEach((c) => {
              const key = String(c.event_id ?? '');
              if (!key) return;
              if (!callsByEvent.has(key)) callsByEvent.set(key, []);
              callsByEvent.get(key).push(c);
            });
            callsByEvent.forEach((arr) => arr.sort(
              (a, b) => String(a.call_start_time || '').localeCompare(String(b.call_start_time || ''))
            ));
            events = events.map((r) => ({
              ...r,
              calls: callsByEvent.get(String(r.event_id ?? '')) || [],
            }));
          }
        }
      } catch (err) {
        console.error('[AreaProfilePage] Call logs merge failed:', err);
      }
    }

    setHourData(hourResult || {});
    setWeekData(weekResult || {});
    setReasonData(reasonResult || {});
    setEventData(events);
    setPrenoteData(prenotes || []);
    setReportLoading(false);
  }, [campusId, areaId, startDate, endDate, eventNotesPreform, isGrandstream]);

  useEffect(() => {
    fetchReportingCycle();
    const interval = setInterval(fetchReportingCycle, 1_200_000);
    return () => clearInterval(interval);
  }, [fetchReportingCycle]);

  // --------------------------------------------------------------------------
  // CAMPUS CHANGE HANDLER
  // --------------------------------------------------------------------------
  const handleCampusChange = useCallback((e) => {
    const val = parseInt(e.target.value, 10);
    updateSessionCampus(val);
    navigate('/community/areas');
  }, [navigate]);

  // --------------------------------------------------------------------------
  // ACTION HANDLERS
  // --------------------------------------------------------------------------

  /** Save basic info (name, type, building, occupancy) */
  const handleBasicInfoSave = useCallback(async (formData) => {
    setSaving(true);
    const session = getSessionParams();
    const payload = [{
      area_id: areaId,
      area_name: formData.areaName,
      area_type_id: parseInt(formData.areaTypeId),
      max_occupancy: parseInt(formData.maxOccupancy),
      building_id: parseInt(formData.buildingId),
      modified_by: getUserId(),
      campus_id: campusId,
      vendor_ids: session.vendor_ids,
    }];
    const oldPayload = [{
      area_id: areaId,
      area_name: areaName,
      area_type_id: areaTypeId,
      max_occupancy: maxOccupancy,
    }];

    const result = await editAreaProfile(payload, oldPayload, session);
    setSaving(false);

    if (result && result[0]) {
      showToast('success', 'Area information saved.');
      setBasicDrawerOpen(false);
      fetchBasicDataCycle();
    } else {
      showToast('error', `Error during save${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [areaId, areaName, areaTypeId, maxOccupancy, campusId, getSessionParams, showToast, fetchBasicDataCycle]);

  /** Remove a device from the area */
  const handleRemoveDevice = useCallback(async () => {
    if (!removeDevice) return;
    // Defense-in-depth: the remove button is already disabled in the UI for
    // restricted devices when the user can't manage them, but block the
    // mutation here too in case it's reached some other way.
    if (isRestrictedDevice(removeDevice) && !canManageRestrictedDevices) {
      showToast('error', 'You do not have permission to remove this device.');
      setRemoveDialogOpen(false);
      setRemoveDevice(null);
      return;
    }
    setSaving(true);
    const session = getSessionParams();
    const result = await removeDeviceFromArea(removeDevice, areaId, session, isGrandstream);
    setSaving(false);

    if (result && result[0]) {
      showToast('success', 'Device has been removed.');
      setRemoveDialogOpen(false);
      setRemoveDevice(null);
      fetchBasicDataCycle();
    } else {
      showToast('error', `Failed to remove device${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [removeDevice, areaId, getSessionParams, showToast, fetchBasicDataCycle, isRestrictedDevice, canManageRestrictedDevices]);

  /** Assign a device to the area. `descriptions` is the optional "Location
   *  within area" captured by the drawer's confirm step; blank leaves whatever
   *  the device already had. */
  const handleAssignDevice = useCallback(async (device, descriptions) => {
    // Defense-in-depth, matching the remove path above.
    if (isRestrictedDevice(device) && !canManageRestrictedDevices) {
      showToast('error', 'You do not have permission to assign this device.');
      return;
    }
    setSaving(true);
    const session = getSessionParams();
    const campusconfig = getCampusConfig();
    /* DeviceAssignDrawer seeds its location field with the device's current
       description, so whatever comes back is authoritative — including an empty
       string, which means the user cleared it deliberately. Passed straight
       through; only callers that never show the field omit the argument. */
    const result = await assignDeviceToArea(device, areaId, areaName, session, campusconfig?.is_grandstream, descriptions);
    setSaving(false);

    if (result && result[0]) {
      showToast('success', `${device.device_name || 'Device'} has been assigned.`);
      setAssignDrawerOpen(false);
      fetchBasicDataCycle();
    } else {
      showToast('error', `Failed to assign device${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [areaId, areaName, getSessionParams, showToast, fetchBasicDataCycle, isRestrictedDevice, canManageRestrictedDevices]);

  /** Quick add: creates the device and assigns it to this area in one call. */
  const handleEasyAddDevice = useCallback(async (formData) => {
    const session = getSessionParams();
    const result = await addDevice(formData, 'device_add_area', session);
    if (result && result[0]) {
      showToast('success', `${formData.device_name || 'Device'} added and assigned`);
      fetchBasicDataCycle();
    }
    return result;
  }, [getSessionParams, showToast, fetchBasicDataCycle]);

  /** Duplicate serial in the easy flow — assign the existing device instead of
   *  erroring, so the user never needs to know whether it was already added. */
  const handleEasyAssignExisting = useCallback(async (device) => {
    /* This path never passes through the assign drawer, so there is no confirmation to
       carry DeviceAlarmWarning: the "existing" device found by serial may be alarming in
       another area, so the success message says the alert stays open instead. */
    const alarming = alertedDeviceIds.has(String(device?.device_id));
    const session = getSessionParams();
    const campusconfig = getCampusConfig();
    const result = await assignDeviceToArea(device, areaId, areaName, session, campusconfig?.is_grandstream);
    if (result && result[0]) {
      showToast('success', `${device.device_name || 'Device'} assigned to ${areaName}`
        + (alarming ? '. It still has an active alert: finish or archive it on the Alerts page.' : ''));
      fetchBasicDataCycle();
    }
    return result;
  }, [areaId, areaName, getSessionParams, showToast, fetchBasicDataCycle, alertedDeviceIds]);

  /** Add event note */
  const handleAddNote = useCallback(async (eventId, noteText) => {
    setSaving(true);
    const session = getSessionParams();
    const noteItems = [{
      event_id: eventId,
      campus_id: campusId,
      note: noteText,
      modified_by: getUserId(),
    }];

    const result = await addEventNote(noteItems, session);
    setSaving(false);

    if (result && result[0] === true) {
      showToast('success', 'Note added successfully.');
      setNoteDialogOpen(false);
      setNoteEventIndex(null);
      fetchReportingCycle();
    } else {
      showToast('error', `Error adding note${result?.[1] ? ': ' + result[1] : ''}.`);
    }
  }, [campusId, getSessionParams, showToast, fetchReportingCycle]);

  /** Open remove device dialog */
  const openRemoveDialog = useCallback((device) => {
    setRemoveDevice(device);
    setRemoveDialogOpen(true);
  }, []);

  /** Open note dialog */
  const openNoteDialog = useCallback((eventIndex) => {
    setNoteEventIndex(eventIndex);
    setNoteDialogOpen(true);
  }, []);

  // --------------------------------------------------------------------------
  // DERIVED VALUES
  // --------------------------------------------------------------------------
  const bannerMeta = useMemo(() => [
    { label: '\u00A0', value: '\u00A0' },   // placeholder — reserved for future use
    { label: 'Building', value: buildingName },
    { label: 'Max Occupancy', value: maxOccupancy ? maxOccupancy.toString() : '—' },
  ], [buildingName, maxOccupancy]);

  // --------------------------------------------------------------------------
  // FEATURE CHIPS — capability indicators shown in the profile banner.
  // --------------------------------------------------------------------------

  // E-Call: the area has one or more nurse-call devices (by device category).
  const hasEcall = useMemo(
    () => (areaDevices || []).some((d) => ECALL_CATEGORY_IDS.has(parseInt(d?.device_category_id, 10))),
    [areaDevices]
  );

  // Room Calling: grandstream campus + a Grandstream Speaker attached to the area.
  const hasRoomCalling = useMemo(() => {
    if (!isGrandstream) return false;
    return areaDevices.some((d) =>
      parseInt(d?.manufacture_id, 10) === SPEAKER_MANUFACTURE_ID &&
      parseInt(d?.device_type_id, 10) === SPEAKER_DEVICE_TYPE_ID
    );
  }, [isGrandstream, areaDevices]);

  // SafelyYou fall protection: gated on the SafelyYou vendor being active, then
  // confirmed by an external-area lookup returning a row for this area.
  const hasSafelyYouVendor = useMemo(
    () => getActiveVendorIdList(campusId).map(Number).includes(SAFELYYOU_VENDOR_ID),
    [campusId]
  );
  const [hasFallProtection, setHasFallProtection] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!hasSafelyYouVendor || !areaId) {
      setHasFallProtection(false);
      return;
    }
    (async () => {
      try {
        // Payload wants comma-delimited strings; this page sends just this
        // area_id and the single SafelyYou vendor_id.
        const result = await fetchExternalAreaData(
          campusId, String(areaId), String(SAFELYYOU_VENDOR_ID)
        );
        if (!cancelled) setHasFallProtection(Array.isArray(result) && result.length > 0);
      } catch (err) {
        console.error('[AreaProfilePage] SafelyYou external-area lookup failed:', err);
        if (!cancelled) setHasFallProtection(false);
      }
    })();
    return () => { cancelled = true; };
  }, [campusId, areaId, hasSafelyYouVendor]);

  const featureChips = useMemo(() => {
    const chips = [];
    if (hasEcall) {
      chips.push({ key: 'e-call', label: 'E-Call', tone: 'primary', icon: BELL_ICON,
        title: 'This room has fixed nurse call (e-call) devices.' });
    }
    if (hasRoomCalling) {
      chips.push({ key: 'room-calling', label: 'Room Calling', tone: 'info', icon: PHONE_ICON,
        title: 'This room has a Grandstream speaker for room calling.' });
    }
    if (hasFallProtection) {
      chips.push({ key: 'fall-protection', label: 'Fall Protection', tone: 'success', icon: SHIELD_ICON,
        title: 'This room has SafelyYou fall protection.' });
    }
    return chips;
  }, [hasEcall, hasRoomCalling, hasFallProtection]);

  // --------------------------------------------------------------------------
  // TAB LIST
  // --------------------------------------------------------------------------
  const tabs = useMemo(() => {
    const t = [
      { key: 'basic', label: 'Basic Info' },
      { key: 'reporting', label: 'Reporting' },
      { key: 'devices', label: 'Devices' },
      { key: 'wellness', label: 'Wellness' },
    ];
    if (showAdminTabs) {
      t.push({ key: 'logs', label: 'Logs' });
    }
    return t;
  }, [showAdminTabs]);

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------
  return (
    <>
      <NavigationV2 />
      <div className="nav2-main">
      <div className="ps-page">
        {/* Toast */}
        <Toast toast={toast} onDismiss={dismissToast} />

        {/* Page Header */}
        <header className="ps-page-header">
          <div className="ps-page-header__inner">
            <div className="ps-breadcrumb-row">
              <nav className="ps-breadcrumb">
                <Link to="/community/areas" className="ps-breadcrumb__text">Areas</Link>
                <span className="ps-breadcrumb__separator">/</span>
                <span className="ps-breadcrumb__current">{areaName || 'Area Profile'}</span>
              </nav>
              <div className="ps-campus-row">
                <label htmlFor="ap-campus-selector" className="ps-campus-label">Campus:</label>
                {campusList.length > 1 ? (
                  <select
                    id="ap-campus-selector"
                    className="ps-campus-select"
                    value={campusId}
                    onChange={handleCampusChange}
                  >
                    {campusList.map((c) => (
                      <option key={c.campus_id} value={c.campus_id}>{c.campus_name}</option>
                    ))}
                  </select>
                ) : (
                  <span className="ps-campus-name">{campusName}</span>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Profile Banner */}
        <ProfileBanner
          name={areaName || ''}
          subtitle={areaTypeName}
          metaFields={bannerMeta}
          chips={featureChips}
          loading={loading}
        />

        {/* Tab Bar */}
        <div className="pp-tab-bar">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`pp-tab${activeTab === tab.key ? ' is-active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="pp-tab-content">
          {/* Outside the activeTab switch on purpose: an alarming room is worth
              seeing from the Reporting or Devices tab too. */}
          <ActiveAlertPanel alerts={areaAlerts} tzOffset={tzOffset} scope="area" />

          {activeTab === 'basic' && (
            <BasicInfoTab
              areaName={areaName}
              areaTypeName={areaTypeName}
              buildingName={buildingName}
              maxOccupancy={maxOccupancy}
              residents={residents}
              areaGroups={areaGroups}
              loading={loading}
              editRights={editRights}
              onEdit={() => setBasicDrawerOpen(true)}
            />
          )}

          {activeTab === 'reporting' && (
            <ProfileReportingTab
              entity="area"
              hourData={hourData}
              weekData={weekData}
              reasonData={reasonData}
              eventData={eventData}
              eventNotesActive={eventNotesActive}
              isReasonEditable={isReasonEditable}
              isGrandstream={isGrandstream}
              tzOffset={tzOffset}
              onAddNote={openNoteDialog}
              entityId={areaId}
            />
          )}

          {activeTab === 'devices' && (
            <DevicesTab
              areaDevices={areaDevices}
              areaName={areaName}
              devEditRights={devEditRights}
              onAssign={() => setAssignDrawerOpen(true)}
              onRemove={openRemoveDialog}
              canManageRestrictedDevices={canManageRestrictedDevices}
              isRestrictedDevice={isRestrictedDevice}
              alertedDeviceIds={alertedDeviceIds}
            />
          )}

          {activeTab === 'wellness' && (
            <WellnessTab
              careData={careData}
              roundsData={roundsData}
              areaGroups={areaGroups}
            />
          )}

          {activeTab === 'logs' && showAdminTabs && (
            <LogsTab
              logData={logData}
              tzOffset={tzOffset}
            />
          )}
        </div>

        {/* ---- Drawers ---- */}

        {/* Basic Info Drawer */}
        {basicDrawerOpen ? (
          <div className="dp-drawer-backdrop" onClick={() => setBasicDrawerOpen(false)} aria-hidden="true" />
        ) : null}
        <BasicInfoDrawer
          open={basicDrawerOpen}
          onClose={() => setBasicDrawerOpen(false)}
          onSave={handleBasicInfoSave}
          saving={saving}
          areaName={areaName}
          areaTypeId={areaTypeId}
          buildingId={buildingId}
          maxOccupancy={maxOccupancy}
          areaTypes={areaTypes}
          buildings={buildings}
        />

        {/* Device Assign Drawer */}
        {assignDrawerOpen ? (
          <div className="dp-drawer-backdrop" onClick={() => setAssignDrawerOpen(false)} aria-hidden="true" />
        ) : null}
        <DeviceAssignDrawer
          open={assignDrawerOpen}
          onClose={() => setAssignDrawerOpen(false)}
          onAssign={handleAssignDevice}
          saving={saving}
          availableDevices={availableDevices}
          areaDevices={areaDevices}
          areaName={areaName}
          canManageRestrictedDevices={canManageRestrictedDevices}
          isRestrictedDevice={isRestrictedDevice}
          onAddNewDevice={() => setEasyAddOpen(true)}
          alertedDeviceIds={alertedDeviceIds}
        />

        {/* Simplified add-and-assign flow for brand-new devices. Rendered after
            the assign drawer on purpose: both sit at --z-modal, so DOM order is
            what puts this one on top. */}
        <EasyAddDeviceDrawer
          isOpen={easyAddOpen}
          onClose={() => setEasyAddOpen(false)}
          mode="area"
          classPrefix="ap"
          roleWeight={myRoleWeight}
          target={{ area_id: areaId, area_name: areaName }}
          existingDevices={availableDevices}
          campusId={campusId}
          sessionId={getSessionId()}
          onAdd={handleEasyAddDevice}
          onAssignExisting={handleEasyAssignExisting}
        />

        {/* Device Remove Dialog */}
        {removeDialogOpen && removeDevice && (
          <DeviceRemoveDialog
            deviceName={removeDevice.device_name || removeDevice.manufacture_uuid || 'Device'}
            areaName={areaName}
            onClose={() => { setRemoveDialogOpen(false); setRemoveDevice(null); }}
            onConfirm={handleRemoveDevice}
            saving={saving}
            alarming={alertedDeviceIds.has(String(removeDevice.device_id))}
          />
        )}

        {/* Event Note Dialog */}
        {noteDialogOpen && noteEventIndex !== null && (
          <EventNoteDialog
            event={eventData[noteEventIndex]}
            prenoteData={prenoteData}
            eventNotesPreform={eventNotesPreform}
            eventNotesFreeform={eventNotesFreeform}
            tzOffset={tzOffset}
            onClose={() => { setNoteDialogOpen(false); setNoteEventIndex(null); }}
            onSave={handleAddNote}
            saving={saving}
          />
        )}
      </div>
      </div>
    </>
  );
};

export default AreaProfilePage;
